<script setup>
import { DEV_MODE } from '@/config/dev-mode';
import { useAnalysisStore } from '@/store/modules/analysis';
import { usePaymentsStore } from '@/store/modules/payments';
import { postAnalysisLogAPI } from '@/api/AnalysisApi';
import { addContentLogAPI } from '@/api/logServiceApi';
import { MONTHLY_FORTUNE_PARENT_TYPE_ID } from './tocCards';
import { useMonthlyFortuneFreeData } from '@/composables/saju-insight/useMonthlyFortuneFreeData';
import ConfirmComponentPay from '@/components/saju-insight/common/confirmComponent.vue';
import AlertComponent from '@/components/common/Alert.vue';

const props = defineProps({
  visible: { type: Boolean, default: false },
  guestUserId: { type: [Number, String], default: null },
});

const emit = defineEmits(['close', 'payment-success']);

const analysisStore = useAnalysisStore();
const paymentsStore = usePaymentsStore();

/** 현재 달 기준 +0/+1/+2개월 */
const monthOptions = computed(() => {
  const list = [];
  const today = new Date();
  for (let i = 0; i < 3; i++) {
    const d = new Date(today.getFullYear(), today.getMonth() + i, 1);
    list.push({ year: d.getFullYear(), month: d.getMonth() + 1 });
  }
  return list;
});

const isMonthPurchased = (year, month) => {
  if (!props.guestUserId) return false;
  return analysisStore.isMonthlyFortuneMonthFullyPurchased?.(props.guestUserId, year, month) || false;
};

// =============== 무료 데이터 기반 경고 체크 (대운범위초과 등) ===============
const guestUserIdRef = computed(() => props.guestUserId);
const probeYear = ref(null);
const probeMonth = ref(null);
const { fetchFor, getFreeDataFor } = useMonthlyFortuneFreeData(
  guestUserIdRef,
  probeYear,
  probeMonth,
  () => [],
);

const extractWarning = (data) => {
  const w = data?.['경고'];
  if (!w) return '';
  if (w['유형'] === '대운범위초과') {
    return w['메시지'] || '분석 기간이 대운 범위를 초과합니다.';
  }
  return w['메시지'] || '';
};

const probeMonthWarning = async (year, month) => {
  if (!props.guestUserId) return '';
  await fetchFor(year, month);
  return extractWarning(getFreeDataFor(year, month));
};

const selected = ref(null);

const selectFirstUnpurchased = () => {
  const found = monthOptions.value.find((o) => !isMonthPurchased(o.year, o.month));
  selected.value = found ? { year: found.year, month: found.month } : null;
};

const bundleTicketCount = computed(() => {
  const ticket = paymentsStore?.analysisTicketsData?.find(
    (t) => t.typeId === MONTHLY_FORTUNE_PARENT_TYPE_ID,
  );
  return ticket?.totalCount || 0;
});

const canConfirm = computed(() => {
  if (!selected.value) return false;
  if (bundleTicketCount.value <= 0) return false;
  return !isMonthPurchased(selected.value.year, selected.value.month);
});

const confirmRef = ref(null);
const isProcessing = ref(false);

// 경고 알림 (사주 자체가 사용 불가한 경우 — 대운범위초과 등)
const warningAlertRef = ref(null);
const warningAlertMessage = ref('');
const warningAlertText = ref('다른 사주로 낭만월운을 이용해주세요.');
const isReady = ref(false);

const onUnavailableAlertConfirm = () => {
  emit('close');
};

const confirmTitle = computed(() => '월운세 분석권을 사용하시겠습니까?');
const confirmWarning = computed(() => '월운세 분석권 1건 사용');

const onClose = () => {
  if (isProcessing.value) return;
  addContentLogAPI({ contentType: 'am_monthly_ticket_popup_action_opt3', option3: '나중에' });
  emit('close');
};

const onSelectOption = (opt) => {
  if (isMonthPurchased(opt.year, opt.month)) return;
  addContentLogAPI({
    contentType: 'am_monthly_ticket_popup_action_opt3',
    option3: `월선택:${opt.year}-${String(opt.month).padStart(2, '0')}`,
  });
  selected.value = { year: opt.year, month: opt.month };
};

const isShaking = ref(false);
const onBackdropClick = () => {
  if (isShaking.value) return;
  isShaking.value = true;
  setTimeout(() => {
    isShaking.value = false;
  }, 350);
};

const onConfirmClick = () => {
  if (!canConfirm.value || isProcessing.value) return;
  addContentLogAPI({ contentType: 'am_monthly_ticket_popup_action_opt3', option3: '사용' });
  nextTick(() => confirmRef.value?.openConfirmDialog());
};

const handlePurchase = async () => {
  if (!selected.value || isProcessing.value) return;
  isProcessing.value = true;
  try {
    const res = await postAnalysisLogAPI({
      guestUserId: props.guestUserId,
      analysisTypeId: MONTHLY_FORTUNE_PARENT_TYPE_ID,
      paymentMethod: 'ticket',
      year: selected.value.year,
      month: selected.value.month,
    });
    if (res?.guestUserId || res?.analysisLogId) {
      // 선택된 월을 보존 (구매 후 해당 월로 이동)
      analysisStore.setMonthlyFortuneSelectedMonth?.(selected.value.year, selected.value.month);
      analysisStore.setMonthlyFortunePreserveSelectedMonth?.();

      await Promise.all([
        // 구매 직후엔 해당 월 access 캐시를 강제 갱신해야 잠금이 즉시 풀림
        analysisStore.fetchMonthlyFortuneLogs?.(
          props.guestUserId,
          selected.value.year,
          selected.value.month,
          { force: true },
        ),
        paymentsStore.fetchTicketCount?.(),
      ]);
      emit('payment-success', true);
      emit('close');
    }
  } catch (e) {
    DEV_MODE && console.error('낭만월운 패키지 결제 실패:', e);
  } finally {
    isProcessing.value = false;
  }
};

watch(
  () => props.visible,
  async (v) => {
    if (!v) {
      isReady.value = false;
      warningAlertMessage.value = '';
      return;
    }
    isReady.value = false;
    warningAlertMessage.value = '';

    // 1) 분석권/구매이력 + 현재월 무료데이터로 경고 여부 확인 (경고는 사주 단위라 한 달이면 충분)
    const firstMonth = monthOptions.value[0];
    const [, , warning] = await Promise.all([
      paymentsStore.fetchTicketCount?.(),
      props.guestUserId
        ? analysisStore.fetchMonthlyFortunePurchaseHistory?.(props.guestUserId)
        : Promise.resolve(),
      probeMonthWarning(firstMonth.year, firstMonth.month),
    ]);

    // 2) 경고가 있으면 사주 자체가 사용 불가 — 팝업 대신 경고 알림만 노출
    if (warning) {
      warningAlertMessage.value = warning;
      nextTick(() => warningAlertRef.value?.openAlertDialog());
      return;
    }

    // 3) 정상 — 팝업 노출 + 첫 미구매 달 선택. 남은 달 데이터는 백그라운드로 fetch
    selectFirstUnpurchased();
    isReady.value = true;
    monthOptions.value
      .slice(1)
      .forEach((opt) => probeMonthWarning(opt.year, opt.month));
  },
  { immediate: true },
);
</script>

<template>
  <div v-if="visible && isReady" class="pop-mf-purchase-backdrop" @click.self="onBackdropClick">
    <div class="pop-mf-purchase" :class="{ 'is-shaking': isShaking }">
      <div class="pop-mf-purchase__header">
        <p class="pop-mf-purchase__title">보고 싶은 달을 선택해주세요.</p>
      </div>
      <div class="pop-mf-purchase__body">
        <div class="pop-mf-purchase__info">
          <span class="pop-mf-purchase__info-label">보유중</span>
          <span class="pop-mf-purchase__info-ticket">월운세 분석권 {{ bundleTicketCount }}매</span>
        </div>
        <div class="pop-mf-purchase__options">
          <div
            v-for="opt in monthOptions"
            :key="`${opt.year}-${opt.month}`"
            class="pop-mf-purchase__option"
            :class="{
              active:
                selected &&
                selected.year === opt.year &&
                selected.month === opt.month &&
                !isMonthPurchased(opt.year, opt.month),
              disabled: isMonthPurchased(opt.year, opt.month),
            }"
            @click="onSelectOption(opt)"
          >
            <p class="pop-mf-purchase__option-label">{{ opt.year }}년 {{ opt.month }}월</p>
            <p v-if="isMonthPurchased(opt.year, opt.month)" class="pop-mf-purchase__option-purchased">
              구매완료
            </p>
            <p v-else class="pop-mf-purchase__option-ticket-use">분석권 1매</p>
          </div>
        </div>
      </div>
      <button
        class="pop-mf-purchase__confirm"
        :class="{ 'pop-mf-purchase__confirm--disabled': !canConfirm }"
        :disabled="!canConfirm"
        type="button"
        @click="onConfirmClick"
      >
        분석권 사용
      </button>
      <button class="pop-mf-purchase__later" type="button" @click="onClose">
        나중에 이용하기
      </button>
    </div>

    <ConfirmComponentPay
      ref="confirmRef"
      :title="confirmTitle"
      text="사용"
      :warning="confirmWarning"
      :doUrl="handlePurchase"
    />
  </div>

  <!-- 사주 자체 사용 불가 (대운범위초과 등) — 팝업 대신 표시 -->
  <AlertComponent
    ref="warningAlertRef"
    :title="warningAlertMessage"
    :text="warningAlertText"
    :doFun="onUnavailableAlertConfirm"
  />
</template>

<style lang="scss" scoped>
.pop-mf-purchase-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
}

@keyframes pop-mf-shake {
  0% { transform: scale(1); }
  40% { transform: scale(1.02); }
  70% { transform: scale(0.99); }
  100% { transform: scale(1); }
}

.pop-mf-purchase {
  background: #fff;
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: min(400px, calc(100vw - 32px));
  padding: 20px 0 16px;
  gap: 12px;
  box-sizing: border-box;

  &__header {
    position: relative;
    width: 100%;
    height: 44px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &__title {
    color: #333;
    font-size: 16px;
    font-weight: 700;
    line-height: 24px;
    margin: 0;
    text-align: center;
  }

  &.is-shaking {
    animation: pop-mf-shake 0.35s ease-out;
  }

  &__body {
    display: flex;
    flex-direction: column;
    gap: 16px;
    align-items: center;
    width: calc(100% - 32px);
  }

  &__info {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    padding: 12px 16px;
    border-radius: 8px;
    background: #f8f8f8;
    box-sizing: border-box;
  }

  &__info-label {
    color: #eb4c10;
    font-size: 12px;
    font-weight: 700;
    line-height: 18px;
  }

  &__info-ticket {
    color: #575757;
    font-size: 14px;
    font-weight: 700;
    line-height: 18px;
  }

  &__options {
    display: flex;
    align-items: stretch;
    justify-content: space-between;
    width: 100%;
    gap: 12px;
  }

  &__option {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    padding: 20px 8px;
    border-radius: 12px;
    border: 1px solid #ebebeb;
    background: #fff;
    flex: 1;
    min-width: 0;
    box-sizing: border-box;
    cursor: pointer;
    transition: all 0.2s;

    &.active {
      border-color: #eb4c10;
      background: #fff8f5;
    }

    &.active .pop-mf-purchase__option-label,
    &.active .pop-mf-purchase__option-ticket-use {
      color: #eb4c10;
      font-weight: 700;
    }

    &.disabled {
      background: #f8f8f8;
      border-color: transparent;
      cursor: default;
    }

    &.disabled .pop-mf-purchase__option-label {
      color: #757575;
      font-weight: 600;
    }
  }

  &__option-label {
    color: #333;
    font-size: 14px;
    font-weight: 500;
    line-height: 18px;
    text-align: center;
    white-space: nowrap;
    margin: 0;
  }

  &__option-ticket-use {
    color: #333;
    font-size: 14px;
    font-weight: 600;
    line-height: 20px;
    margin: 0;
  }

  &__option-purchased {
    color: #757575;
    font-size: 15px;
    font-weight: 600;
    line-height: 24px;
    margin: 0;
  }

  &__confirm {
    width: calc(100% - 32px);
    padding: 14px 20px;
    border-radius: 6px;
    background: #eb4c10;
    color: #fff;
    font-size: 15px;
    font-weight: 700;
    line-height: 20px;
    border: none;
    cursor: pointer;
    margin-top: 10px;

    &:hover {
      opacity: 0.9;
    }

    &--disabled {
      background: #949494;
      cursor: default;

      &:hover {
        opacity: 1;
      }
    }
  }

  &__later {
    background: none;
    border: none;
    color: #757575;
    font-size: 14px;
    font-weight: 500;
    line-height: 20px;
    text-decoration: underline;
    text-underline-offset: 3px;
    cursor: pointer;
    padding: 0;
    margin: 0;
  }
}
</style>
